import { FC, useMemo } from 'react';
import CartoesDoCenario, { type RecorteDoCenario } from '@/components/seop/CartoesDoCenario';
import type { SeopCalc } from '@/types/seop';
import {
  linhasDeProducao,
  linhasVisiveis,
  rotuloDoRecorte,
  totalDasLinhas,
  type FiltroDoCenario,
} from '@/utils/seopCenario';
import TabelaEconomica from './TabelaEconomica';

interface PainelDoPeriodoProps {
  /** O cálculo em tela: o do plano atual ou o de um cenário salvo. `null` = carregando. */
  calc: SeopCalc | null;
  titulo: string;
  mes: string;
  filtro: FiltroDoCenario;
  onFiltro: (f: FiltroDoCenario) => void;
}

/**
 * Cartões + tabela econômica, sempre falando do MESMO conjunto de linhas.
 *
 * É aqui que o filtro vira recorte: com busca ou linha de produção escolhida, os cartões e
 * o "Total exibido" somam só o que a tabela mostra (`totalDasLinhas`, a regra do motor).
 * Sem recorte, o total é o `economics.total` que o backend manda — nada é recalculado.
 */
const PainelDoPeriodo: FC<PainelDoPeriodoProps> = ({ calc, titulo, mes, filtro, onFiltro }) => {
  const linhas = useMemo(() => calc?.economics.rows ?? [], [calc]);
  const visiveis = useMemo(() => linhasVisiveis(linhas, filtro), [linhas, filtro]);

  if (!calc) {
    return (
      <div data-testid="seop-abrindo-cenario" className="rounded-xl border border-theme-border bg-surface-card p-5 text-sm text-content-muted">
        Abrindo o cenário…
      </div>
    );
  }

  const recortado = visiveis.length !== linhas.length;
  const total = recortado ? totalDasLinhas(visiveis) : calc.economics.total;
  const recorte: RecorteDoCenario | undefined = recortado
    ? { rotulo: rotuloDoRecorte(filtro), itensDoPeriodo: linhas.length, totalDoPeriodo: calc.economics.total }
    : undefined;

  return (
    <>
      <CartoesDoCenario total={total} itens={visiveis.length} mes={mes} recorte={recorte} />
      <TabelaEconomica
        titulo={titulo}
        linhas={visiveis}
        totalDeLinhas={linhas.length}
        totalExibido={total}
        totais={calc.economics.segment_totals}
        filtro={filtro}
        linhasDeProducaoDisponiveis={linhasDeProducao(linhas)}
        onFiltro={onFiltro}
      />
    </>
  );
};

export default PainelDoPeriodo;
